import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Fragment, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { database } from "../../../../appWrite"; // Adjust this import based on your setup
import { fetchFAQs } from "../../../../redux/slices/FaqSlice";
import { useAppDispatch } from "../../../../redux/hooks";

interface EditFAQModalProps {
  isOpen: boolean;
  onClose: () => void;
  faqData: { id: string; question: string; answer: string } | null;
}

interface FAQData {
  question: string;
  answer: string;
}

export default function EditFAQModal({
  isOpen,
  onClose,
  faqData,
}: EditFAQModalProps) {
  const dispatch = useAppDispatch();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FAQData>();

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (faqData) {
      reset({
        question: faqData.question,
        answer: faqData.answer,
      });
    }
  }, [faqData, reset]);

  const onSubmit = async (data: FAQData) => {
    if (!faqData) return;
    setIsLoading(true);
    try {
      await database.updateDocument(
        "67ce8a4a0032e13c6368", // Database ID
        "67d7ec6f0022005176aa", // Collection ID
        faqData.id,
        data
      );
      alert("FAQ updated successfully!");
      dispatch(fetchFAQs());
      onClose();
    } catch (error) {
      console.error("Error updating FAQ:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <TransitionChild
            as={Fragment}
            enter="transition ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
              <DialogTitle className="text-lg font-semibold mb-4">
                Edit FAQ
              </DialogTitle>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Question
                  </label>
                  <input
                    {...register("question", {
                      required: "Question is required",
                    })}
                    className="mt-1 p-2 w-full border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  {errors.question && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.question.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Answer
                  </label>
                  <textarea
                    {...register("answer", { required: "Answer is required" })}
                    className="mt-1 p-2 w-full border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  {errors.answer && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.answer.message}
                    </p>
                  )}
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`px-4 py-2 bg-primary text-white rounded-lg hover:bg-opacity-90 ${
                      isLoading && "animate-pulse"
                    }`}
                  >
                    {isLoading ? "Updating..." : "Update FAQ"}
                  </button>
                </div>
              </form>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
